import * as React from 'react';

import { ArrowRightOutlined, BranchesOutlined, CalendarOutlined, CodeSandboxOutlined } from '@ant-design/icons';
import { Button, Tooltip } from 'antd';

import { dateFormat } from 'v2/formatters/date';

type EnvironmentVariant = 'front' | 'api' | 'vertical';

type EnvironmentProps = {
  name: string;
  env: string;
  branch: string;
  date: string;
  redirect: string;
  variant?: EnvironmentVariant;
  hidden?: boolean;
};

type DetailLineProps = {
  icon: React.ReactNode;
  label: string;
  value: string;
};

const VARIANT_THEME: Record<EnvironmentVariant, { badge: string; border: string; background: string; label: string }> =
  {
    front: {
      badge: 'bg-blue-100 text-blue-700',
      border: 'border-blue-100',
      background: 'bg-blue-50',
      label: 'Front'
    },
    api: {
      badge: 'bg-green-100 text-green-700',
      border: 'border-green-100',
      background: 'bg-green-50',
      label: 'API'
    },
    vertical: {
      badge: 'bg-purple-100 text-purple-700',
      border: 'border-purple-100',
      background: 'bg-purple-50',
      label: 'Vertical'
    }
  };

function getValue(value: string, hidden?: boolean) {
  if (hidden || !value) {
    return '-';
  }

  return value;
}

function getDateValue(date: string, hidden?: boolean) {
  if (hidden || !date) {
    return '-';
  }

  return dateFormat(date, 'dd/MM/yyyy HH:mm:ss');
}

function DetailLine({ icon, label, value }: DetailLineProps) {
  return (
    <p className='mb-1! flex items-center gap-2'>
      <span className='text-gray-500'>{icon}</span>
      <span className='font-semibold'>{label}:</span>
      <span className='truncate'>{value}</span>
    </p>
  );
}

function Details({ name, env, branch, date, redirect, variant = 'front', hidden = false }: EnvironmentProps) {
  const theme = VARIANT_THEME[variant];

  return (
    <article className='min-w-[300px] rounded-sm bg-white px-4 py-2 shadow-lg last:pb-4'>
      <section className={`rounded-lg border ${theme.border} ${theme.background} flex items-center p-4`}>
        <section className='min-w-0 flex-1'>
          <header className='mb-4 flex items-center gap-2'>
            <span className='self-center truncate text-base font-semibold text-gray-900'>{name}</span>
            <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${theme.badge}`}>{theme.label}</span>
          </header>

          <section className='flex flex-col text-sm text-gray-700'>
            <DetailLine icon={<CodeSandboxOutlined />} label='Env' value={getValue(env, hidden)} />
            <DetailLine icon={<BranchesOutlined />} label='Branch' value={getValue(branch, hidden)} />
            <DetailLine icon={<CalendarOutlined />} label='Data de Atualização' value={getDateValue(date, hidden)} />
          </section>
        </section>

        <a href={redirect} target='_blank' rel='noreferrer' aria-label={`Abrir ${name}`}>
          <Tooltip title='Abrir no GitHub'>
            <Button shape='circle' type='text' icon={<ArrowRightOutlined />} className='bg-white shadow-sm' />
          </Tooltip>
        </a>
      </section>
    </article>
  );
}

export { Details };
